import { ThemeEvents } from '@theme/events';

export const GIFT_PRICE_LINE_PROPERTY = '_giftPrice';
const NON_GIFT_ITEMS_CART_ATTRIBUTE = '_nonGiftItems';

/** @param {unknown} v */
function cartAttributeEntryToString(v) {
  if (v == null || v === '') return '';
  if (Array.isArray(v)) return JSON.stringify(v);
  return String(v);
}

/**
 * Tier auto-gifts use `_giftPrice` on the line; progress and this attribute ignore those lines.
 * @param {{ properties?: Record<string, unknown> | null | { name?: string; key?: string }[] }} line
 */
export function lineItemIsProgressGift(line) {
  const props = line.properties;
  if (!props) return false;
  if (Array.isArray(props)) {
    return props.some(
      (p) =>
        p != null &&
        typeof p === 'object' &&
        (p.name === GIFT_PRICE_LINE_PROPERTY || p.key === GIFT_PRICE_LINE_PROPERTY)
    );
  }
  if (typeof props !== 'object') return false;
  const val = props[GIFT_PRICE_LINE_PROPERTY];
  return val != null && String(val).trim() !== '';
}

/**
 * @param {{ items?: { final_line_price?: number, properties?: Record<string, unknown> | null | { name?: string; key?: string }[] }[] }} cartSnap
 */
export function nonGiftSubtotalCents(cartSnap) {
  return (cartSnap.items ?? []).reduce((sum, line) => {
    if (lineItemIsProgressGift(line)) return sum;
    const cents = Number(line.final_line_price);
    return sum + (Number.isFinite(cents) ? cents : 0);
  }, 0);
}

/**
 * `_nonGiftItems` value: **sum** of all non-gift lines’ `final_line_price` in **major currency units** (cart cents ÷ 100), same basis as gift progress. No brackets. Empty string when there are no non-gift lines.
 * @param {{ items?: { final_line_price?: number, properties?: Record<string, unknown> | null | { name?: string; key?: string }[] }[] }} cartSnap
 */
function nonGiftItemsAttributeMajorValue(cartSnap) {
  const hasNonGift = (cartSnap.items ?? []).some((line) => !lineItemIsProgressGift(line));
  if (!hasNonGift) return '';
  const totalCents = nonGiftSubtotalCents(cartSnap);
  const major = totalCents / 100;
  return String(major).replace(/[\[\]]/g, '');
}

/**
 * Skip POST only when the stored string is already the canonical major-units value.
 * Do **not** skip when legacy shapes match numerically — we must rewrite to a single major string.
 * @param {string} storedRaw
 * @param {string} desiredMajorStr
 */

/**
 * Stored value must be a single major-units string (e.g. "24.65"). Bracket arrays, comma-separated cent tuples, etc. always need a rewrite POST.
 * @param {string} s0
 */
function isNonCanonicalNonGiftStoredFormat(s0) {
  const t = String(s0 ?? '').trim();
  if (!t) return false;
  if (/[\[\]]/.test(t)) return true;
  if (/^\d+(,\d+)+$/.test(t)) return true;
  return false;
}

/** @param {string} storedRaw @param {string} desiredMajorStr */
function shouldSkipNonGiftAttributeSync(storedRaw, desiredMajorStr) {
  const s0 = String(storedRaw ?? '').trim();
  if (desiredMajorStr === '') return s0 === '';
  if (s0 === desiredMajorStr) return true;
  if (isNonCanonicalNonGiftStoredFormat(s0)) return false;

  const d = Number(desiredMajorStr);
  if (!Number.isFinite(d)) return false;

  const asNumber = Number(s0);
  if (!Number.isFinite(asNumber)) return false;

  // Already saved as major units (e.g. "14.79" or "15")
  if (Math.abs(asNumber - d) < 1e-9) return true;

  // Plain integer legacy cents "1479" → need rewrite to "14.79"
  if (!s0.includes('.') && asNumber === Math.round(asNumber) && Math.abs(asNumber / 100 - d) < 1e-9) {
    return false;
  }

  return false;
}

/**
 * `_nonGiftItems` payload: single major-units string. Legacy shapes:
 * - `[1972]` or `[493,1972]` (JSON array of line cents) → sum ÷ 100
 * - `493,1972` (comma cents, no brackets) → sum ÷ 100
 * @param {unknown} val
 */
function sanitizeNonGiftItemsForPost(val) {
  let s = String(val ?? '').trim();
  if (!s) return '';

  if (s.startsWith('[') && s.endsWith(']')) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed) && parsed.length > 0) {
        let sum = 0;
        for (const x of parsed) {
          const n = typeof x === 'number' ? x : Number(String(x).trim());
          if (!Number.isFinite(n)) {
            sum = NaN;
            break;
          }
          sum += n;
        }
        if (Number.isFinite(sum)) {
          return String(sum / 100).replace(/[\[\]]/g, '').trim();
        }
      }
    } catch {
      /* fall through to single-number bracket form */
    }
    const m = /^\[\s*([\d.]+)\s*\]$/.exec(s);
    if (m) {
      const inner = (m[1] ?? '').trim();
      if (!inner.includes('.')) {
        const cents = Number(inner);
        s = Number.isFinite(cents) ? String(cents / 100) : inner;
      } else {
        s = inner;
      }
    }
  } else if (/^\d+(,\d+)+$/.test(s)) {
    const parts = s.split(',').map((p) => Number(p.trim()));
    if (parts.every((n) => Number.isFinite(n))) {
      const sum = parts.reduce((a, b) => a + b, 0);
      s = String(sum / 100);
    }
  }

  return s.replace(/[\[\]]/g, '').trim();
}

/** @param {unknown} parsed @param {boolean} responseOk */
function cartUpdateResponseFailed(parsed, responseOk) {
  if (!responseOk) return true;
  const e = /** @type {{ errors?: unknown }} */ (parsed)?.errors;
  if (e == null) return false;
  if (typeof e === 'string') return e.length > 0;
  if (Array.isArray(e)) return e.length > 0;
  if (typeof e === 'object') return Object.keys(e).length > 0;
  return Boolean(e);
}

/**
 * @param {Record<string, string>} mergedAttributes
 * @returns {Record<string, string>}
 */
function cleanedAttributesForCartUpdateJson(mergedAttributes) {
  /** @type {Record<string, string>} */
  const cleaned = {};
  for (const [k, val] of Object.entries(mergedAttributes)) {
    cleaned[k] =
      k === NON_GIFT_ITEMS_CART_ATTRIBUTE
        ? sanitizeNonGiftItemsForPost(val)
        : String(val).replace(/[\[\]]/g, '');
  }
  return cleaned;
}

/**
 * @param {{ items?: { final_line_price?: number, properties?: Record<string, unknown> | null | { name?: string; key?: string }[] }[], attributes?: Record<string, unknown> | null }} cartSnap
 */
function nonGiftMergeStateFromCartSnap(cartSnap) {
  const nonGiftItemsValue = nonGiftItemsAttributeMajorValue(cartSnap);
  const raw = cartSnap.attributes;
  /** @type {Record<string, string>} */
  const existingAttrs =
    raw != null && typeof raw === 'object' && !Array.isArray(raw)
      ? Object.fromEntries(
          Object.entries(/** @type {Record<string, unknown>} */ (raw)).map(([k, v]) => [
            k,
            cartAttributeEntryToString(v),
          ])
        )
      : {};
  const storedRaw = existingAttrs[NON_GIFT_ITEMS_CART_ATTRIBUTE] ?? '';
  const mergedAttributes = {
    ...existingAttrs,
    [NON_GIFT_ITEMS_CART_ATTRIBUTE]: sanitizeNonGiftItemsForPost(nonGiftItemsValue),
  };
  return { nonGiftItemsValue, storedRaw, mergedAttributes };
}

/**
 * After `/cart/add.js` or `/cart/change.js`, immediately POST `/cart/update.js` with merged `attributes` (including `_nonGiftItems`) so integrations that read cart attributes stay in sync. Optional `sections` / `sections_url` refresh rendered sections in one hop.
 *
 * @param {unknown} cartSnap - Cart JSON from the prior mutation response (needs `items` + `final_line_price` where applicable).
 * @param {{ sections?: unknown, sections_url?: string }} [options]
 * @returns {Promise<{ skipped: boolean, ok: boolean, parsed: unknown }>}
 */
async function patchCartGiftProgressAttributeAfterMutation(cartSnap, options = {}) {
  if (typeof Theme === 'undefined' || !Theme?.routes?.cart_url) {
    return { skipped: true, ok: true, parsed: cartSnap };
  }
  if (!cartSnap || typeof cartSnap !== 'object') {
    return { skipped: true, ok: true, parsed: cartSnap };
  }
  const snap =
    /** @type {{ items?: unknown[], attributes?: Record<string, unknown> | null }} */ (cartSnap);
  if (!Array.isArray(snap.items)) {
    return { skipped: true, ok: true, parsed: cartSnap };
  }

  const mergeStateCart =
    /** @type {{ items?: { final_line_price?: number, properties?: Record<string, unknown> | null | { name?: string; key?: string }[] }[], attributes?: Record<string, unknown> | null }} */ (
      cartSnap
    );
  const { nonGiftItemsValue, storedRaw, mergedAttributes } = nonGiftMergeStateFromCartSnap(mergeStateCart);
  if (shouldSkipNonGiftAttributeSync(storedRaw, nonGiftItemsValue)) {
    return { skipped: true, ok: true, parsed: cartSnap };
  }

  const cartUpdateUrl = resolveCartUpdateJsUrl();
  if (!cartUpdateUrl) {
    return { skipped: true, ok: true, parsed: cartSnap };
  }

  /** @type {Record<string, unknown>} */
  const bodyObj = {
    attributes: cleanedAttributesForCartUpdateJson(mergedAttributes),
  };

  const { sections, sections_url } = options;
  if (
    sections !== undefined &&
    sections !== null &&
    sections !== '' &&
    !(Array.isArray(sections) && sections.length === 0)
  ) {
    bodyObj.sections = sections;
  }
  if (
    sections_url !== undefined &&
    sections_url !== null &&
    String(sections_url).trim() !== ''
  ) {
    bodyObj.sections_url = sections_url;
  }

  const expectedMajor = mergedAttributes[NON_GIFT_ITEMS_CART_ATTRIBUTE];

  try {
    const postOnce = async () => {
      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(bodyObj),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        return { ok: false, parsed: null };
      }
      return { ok: !cartUpdateResponseFailed(parsed, response.ok), parsed };
    };

    let { ok, parsed } = await postOnce();
    if (!ok || parsed == null) {
      return { skipped: false, ok: false, parsed: cartSnap };
    }

    if (cartAttributeHasLegacyBrackets(parsed, expectedMajor)) {
      ({ ok, parsed } = await postOnce());
      if (!ok || parsed == null) {
        return { skipped: false, ok: true, parsed: cartSnap };
      }
    }

    return { skipped: false, ok: true, parsed };
  } catch {
    return { skipped: false, ok: false, parsed: cartSnap };
  }
}

export { patchCartGiftProgressAttributeAfterMutation };

function resolveCartUpdateJsUrl() {
  const u = Theme?.routes?.cart_update_url;
  if (u) {
    let s = String(u).trim().replace(/\.js\.js$/i, '.js');
    if (s.endsWith('.js')) return s;
    return s.replace(/\/?$/, '') + '.js';
  }
  const cart = Theme?.routes?.cart_url;
  if (!cart) return '';
  const base = String(cart).replace(/\.js$/i, '').replace(/\/?$/, '');
  return `${base}/update.js`;
}

/**
 * POST `/cart/update.js`: try `attributes[key]=value` (form) first, then JSON `{ attributes }` as fallback.
 * @param {string} cartUpdateUrl
 * @param {Record<string, string>} attributes
 * @param {'form' | 'json'} encoding
 */
async function postCartAttributes(cartUpdateUrl, attributes, encoding) {
  /** @type {RequestInit} */
  const init = {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  };

  if (encoding === 'json') {
    /** @type {Record<string, string>} */
    const cleaned = cleanedAttributesForCartUpdateJson(
      /** @type {Record<string, string>} */ (attributes)
    );
    init.headers = /** @type {HeadersInit} */ ({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    init.body = JSON.stringify({ attributes: cleaned });
  } else {
    const body = new URLSearchParams();
    for (const [key, val] of Object.entries(attributes)) {
      const clean =
        key === NON_GIFT_ITEMS_CART_ATTRIBUTE
          ? sanitizeNonGiftItemsForPost(val)
          : String(val).replace(/[\[\]]/g, '');
      body.set(`attributes[${key}]`, clean);
    }
    init.body = body;
  }

  const response = await fetch(cartUpdateUrl, init);
  const responseText = await response.text();
  let parsed;
  try {
    parsed = JSON.parse(responseText);
  } catch {
    return { ok: false, parsed: null };
  }
  return { ok: response.ok, parsed };
}

/**
 * @param {unknown} cartSnap
 * @param {string} expectedMajor
 */
function cartAttributeHasLegacyBrackets(cartSnap, expectedMajor) {
  const raw = /** @type {{ attributes?: Record<string, unknown> }} */ (cartSnap)?.attributes?.[
    NON_GIFT_ITEMS_CART_ATTRIBUTE
  ];
  const s = raw != null ? String(raw).trim() : '';
  if (isNonCanonicalNonGiftStoredFormat(s)) return true;
  if (/[\[\]]/.test(s)) return true;
  if (expectedMajor !== '' && s !== expectedMajor) {
    const n = Number(s);
    const d = Number(expectedMajor);
    if (Number.isFinite(n) && Number.isFinite(d) && !s.includes('.') && Math.abs(n / 100 - d) < 1e-9) return true;
  }
  return false;
}

/**
 * Writes `cart.attributes._nonGiftItems`: **sum** of non-gift lines’ post-discount totals (`final_line_price`) in **major units** (same basis as gift-progress bar). No `[]`.
 * Merges with existing attributes. Safe to call on every cart/discount change.
 */
let nonGiftLateRetryCount = 0;
const NON_GIFT_LATE_RETRY_MAX = 5;

export async function syncNonGiftItemsCartAttribute() {
  if (typeof Theme === 'undefined' || !Theme?.routes?.cart_url) {
    return;
  }

  const cartUpdateUrl = resolveCartUpdateJsUrl();
  if (!cartUpdateUrl) {
    return;
  }

  /** @type {{ items?: { final_line_price?: number, properties?: Record<string, unknown> | null | { name?: string; key?: string }[] }[], attributes?: Record<string, unknown> | null }} */
  let cartSnap;
  try {
    cartSnap = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' }).then((r) =>
      r.json()
    );
  } catch {
    return;
  }

  const { nonGiftItemsValue, storedRaw, mergedAttributes } = nonGiftMergeStateFromCartSnap(cartSnap);
  if (shouldSkipNonGiftAttributeSync(storedRaw, nonGiftItemsValue)) {
    return;
  }
  const expectedMajor = mergedAttributes[NON_GIFT_ITEMS_CART_ATTRIBUTE];

  const fetchCartJs = () =>
    fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' }).then((r) => r.json());

  try {
    let { ok, parsed } = await postCartAttributes(cartUpdateUrl, mergedAttributes, 'form');
    if (parsed == null || cartUpdateResponseFailed(parsed, ok)) {
      ({ ok, parsed } = await postCartAttributes(cartUpdateUrl, mergedAttributes, 'json'));
      if (parsed == null || cartUpdateResponseFailed(parsed, ok)) {
        return;
      }
    }

    let verifySnap;
    try {
      verifySnap = await fetchCartJs();
    } catch {
      return;
    }

    if (cartAttributeHasLegacyBrackets(verifySnap, expectedMajor)) {
      ({ ok, parsed } = await postCartAttributes(cartUpdateUrl, mergedAttributes, 'json'));
      if (parsed == null || cartUpdateResponseFailed(parsed, ok)) {
        return;
      }
      try {
        verifySnap = await fetchCartJs();
      } catch {
        return;
      }
    }

    if (!cartAttributeHasLegacyBrackets(verifySnap, expectedMajor)) {
      nonGiftLateRetryCount = 0;
      return;
    }

    if (nonGiftLateRetryCount < NON_GIFT_LATE_RETRY_MAX) {
      nonGiftLateRetryCount += 1;
      const n = nonGiftLateRetryCount;
      setTimeout(() => enqueueSyncNonGiftCartAttribute(), 450 + n * 150);
    }
  } catch {
    /* ignore */
  }
}

/** @type {Promise<void>} */
let syncChain = Promise.resolve();

function enqueueSyncNonGiftCartAttribute() {
  syncChain = syncChain.then(() => syncNonGiftItemsCartAttribute()).catch(() => {});
}

/**
 * Runs on every cart add/update and discount change, independent of cart drawer.
 */
function initNonGiftCartAttributeSync() {
  document.addEventListener(
    ThemeEvents.cartUpdate,
    (event) => {
      if (!(event instanceof Event)) return;
      // CartUpdateEvent / CartAddEvent extend Event (not CustomEvent). Avoid instanceof Cart* — duplicate bundles break it.
      const data = /** @type {Event & { detail?: { data?: { didError?: boolean } } }} */ (event).detail?.data;
      if (data && typeof data === 'object' && data.didError === true) return;
      enqueueSyncNonGiftCartAttribute();
    },
    false
  );

  document.addEventListener(ThemeEvents.discountUpdate, () => enqueueSyncNonGiftCartAttribute(), false);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enqueueSyncNonGiftCartAttribute(), { once: true });
  } else {
    enqueueSyncNonGiftCartAttribute();
  }
}

initNonGiftCartAttributeSync();
